import { Filters, MetaData } from "./"
import { CreateModuleApiRequest, Module } from "./modules"

export const STARTING_LEVELS = [
  "A0",
  "A0_till_A1",
  "A1",
  "A1_till_A2",
  "A2",
  "A2_till_A2_plus",
  "A2_plus",
  "A2_plus_till_B1",
  "B1",
  "B1_till_B1_plus",
  "B1_plus",
  "B1_plus_till_B2",
  "B2",
  "NT1_instroom",
  "Instroom_1F",
  "OneF_till_2F",
] as const

export const END_GOALS = [
  "Zelfredzaamheid",
  "A1",
  "A2 (B1)",
  "B1",
  "B2",
  "n.v.t.",
] as const

export type EndGoal = (typeof END_GOALS)[number]
export type StartingLevel = (typeof STARTING_LEVELS)[number]
import { z } from "zod"

export const startingLevelEnum = z.enum(STARTING_LEVELS)

export const learningPathEnum = z.enum(
  [
    "one",
    "two",
    "three",
    "three_m",
    "four",
    "four_m",
    "five",
    "seven",
    "tihb",
    "map",
    "pvt",
    "participatie_cursus",
    "kbm",
    "ona",
    "gezond_bewegen",
  ],
  { message: "Leerlijn optie is verplicht" },
)

export type LearningPathOption = z.infer<typeof learningPathEnum>

export const learningPathOptions: {
  value: LearningPathOption
  label: string
}[] = [
  { value: "one", label: "1" },
  { value: "two", label: "2" },
  { value: "three", label: "3" },
  { value: "three_m", label: "3M" },
  { value: "four", label: "4" },
  { value: "four_m", label: "4M" },
  { value: "five", label: "5" },
  { value: "seven", label: "7" },
  { value: "tihb", label: "TIHB" },
  { value: "map", label: "MAP" },
  { value: "pvt", label: "PVT" },
  { value: "participatie_cursus", label: "Participatie cursus" },
  { value: "kbm", label: "KBM" },
  { value: "ona", label: "ONA" },
  { value: "gezond_bewegen", label: "Gezond bewegen" },
]

export const learningPathCategory = z.enum(
  [
    "inb_taalverhoging",
    "taaloffensief_tof",
    "web_stedendriehoek_noordwest_veluwe",
    "web_valleiregio",
    "web_duin_bollenstreek",
    "vrije_cursus",
  ],
  { message: "Leerlijn type is verplicht" },
)

export const learningPathCategories: {
  key: LearningPathCategory
  label: string
}[] = [
  { key: "inb_taalverhoging", label: "INB Taalverhoging" },
  { key: "taaloffensief_tof", label: "Taaloffensief (TOF)" },
  {
    key: "web_stedendriehoek_noordwest_veluwe",
    label: "WEB SD-NwV",
  },
  { key: "web_valleiregio", label: "WEB Valleiregio (VR)" },
  { key: "web_duin_bollenstreek", label: "WEB Duin & Bollenstreek (DBS)" },
  { key: "vrije_cursus", label: "Vrije cursus" },
]

export const learningPathCategoryOptions: {
  value: LearningPathCategory
  label: string
}[] = [
  { value: "inb_taalverhoging", label: "INB Taalverhoging" },
  { value: "taaloffensief_tof", label: "Taaloffensief (TOF)" },
  {
    value: "web_stedendriehoek_noordwest_veluwe",
    label: "WEB Stedendriehoek/ Noordwest Veluwe (SD-NwV)",
  },
  { value: "web_valleiregio", label: "WEB Valleiregio (VR)" },
  { value: "web_duin_bollenstreek", label: "WEB Duin & Bollenstreek (DBS)" },
  { value: "vrije_cursus", label: "Vrije cursus" },
]

export type LearningPathCategory = z.infer<typeof learningPathCategory>

export const learningPathCategoryMap: Record<LearningPathCategory, string> = {
  inb_taalverhoging: "INB Taalverhoging",
  taaloffensief_tof: "Taaloffensief (TOF)",
  web_stedendriehoek_noordwest_veluwe:
    "WEB Stedendriehoek/ Noordwest Veluwe (SD-NwV)",
  web_valleiregio: "WEB Valleiregio (VR)",
  web_duin_bollenstreek: "WEB Duin & Bollenstreek (DBS)",
  vrije_cursus: "Vrije cursus",
}

export const numberedLearningPathOptions = learningPathOptions.filter((o) =>
  [
    "one",
    "two",
    "three",
    "three_m",
    "four",
    "four_m",
    "five",
    "seven",
  ].includes(o.value),
)

export const freeCourselearningPathOptions = learningPathOptions.filter((o) =>
  [
    "tihb",
    "map",
    "pvt",
    "participatie_cursus",
    "kbm",
    "ona",
    "gezond_bewegen",
  ].includes(o.value),
)

export const webLearningPathCategories: LearningPathCategory[] = [
  "web_stedendriehoek_noordwest_veluwe",
  "web_valleiregio",
  "web_duin_bollenstreek",
]

export const TaaloffensiefRoutes = z.enum(
  [
    "taal_participatie",
    "taal_ouderbetrokkenheid",
    "taal_participatie_geletterd",
    "taal_werk_opleiding",
  ],
  { message: "Doelgroep is verplicht" },
)

export type TaaloffensiefRoute = z.infer<typeof TaaloffensiefRoutes>

export type TaaloffensiefRouteOptions = typeof taaloffensiefRoutesOptions

export const taaloffensiefRouteMap: Record<TaaloffensiefRoute, string> = {
  taal_participatie: "Taal & Participatie",
  taal_ouderbetrokkenheid: "Taal & Ouderbetrokkenheid",
  taal_participatie_geletterd: "Taal & Participatie Geletterd",
  taal_werk_opleiding: "Taal & Werk Opleiding",
}

export const taaloffensiefRoutesOptions: {
  value: TaaloffensiefRoute
  label: string
}[] = [
  { value: "taal_participatie", label: "Taal & Participatie" },
  { value: "taal_ouderbetrokkenheid", label: "Taal & Ouderbetrokkenheid" },
  {
    value: "taal_participatie_geletterd",
    label: "Taal & Participatie Geletterd",
  },
  { value: "taal_werk_opleiding", label: "Taal & Werk Opleiding" },
]

export const ROUTE_REQUIRED_CATEGORIES: LearningPathCategory[] = [
  "taaloffensief_tof",
]

export const startingLevelOptions: {
  value: StartingLevel
  label: string
}[] = [
  { value: "A0", label: "A0" },
  { value: "A0_till_A1", label: "tussen A0 en A1" },
  { value: "A1", label: "A1" },
  { value: "A1_till_A2", label: "tussen A1 en A2" },
  { value: "A2", label: "A2" },
  { value: "A2_till_A2_plus", label: "tussen A2 en A2+" },
  { value: "A2_plus", label: "A2+" },
  { value: "A2_plus_till_B1", label: "tussen A2+ en B1" },
  { value: "B1", label: "B1" },
  { value: "B1_till_B1_plus", label: "tussen B1 en B1+" },
  { value: "B1_plus", label: "B1+" },
  { value: "B1_plus_till_B2", label: "tussen B1+ en B2" },
  { value: "B2", label: "B2" },
  { value: "NT1_instroom", label: "< NT1 instroom" },
  { value: "Instroom_1F", label: "Instroom-1F" },
  { value: "OneF_till_2F", label: "tussen 1F en 2F" },
]

export const startLevelMap: Record<StartingLevel, string> = {
  A0: "A0",
  A0_till_A1: "tussen A0 en A1",
  A1: "A1",
  A1_till_A2: "tussen A1 en A2",
  A2: "A2",
  A2_till_A2_plus: "tussen A2 en A2+",
  A2_plus: "A2+",
  A2_plus_till_B1: "tussen A2+ en B1",
  B1: "B1",
  B1_till_B1_plus: "tussen B1 en B1+",
  B1_plus: "B1+",
  B1_plus_till_B2: "tussen B1+ en B2",
  B2: "B2",
  NT1_instroom: "< NT1 instroom",
  Instroom_1F: "Instroom-1F",
  OneF_till_2F: "tussen 1F en 2F",
}

export const endGoalOptions: { value: EndGoal; label: string }[] = [
  { value: "Zelfredzaamheid", label: "Zelfredzaamheid" },
  { value: "A1", label: "A1" },
  { value: "A2 (B1)", label: "A2 (B1)" },
  { value: "B1", label: "B1" },
  { value: "B2", label: "B2" },
  { value: "n.v.t.", label: "N.v.t." },
]

export const learnabilityScoreGeneralEnum = z.enum(
  [
    "zero",
    "one_to_twelve",
    "thirteen_or_higher",
    "eighteen_or_higher",
    "twenty_or_higher",
    "not_applicable",
  ],
  { message: "Leerbaarheidsscore is verplicht" },
)

export const learnabilityScoreWebFormeelEnum = z.enum(
  ["ten_to_fifteen", "sixteen_or_higher", "twenty_or_higher", "not_applicable"],
  { message: "Leerbaarheidsscore is verplicht" },
)

export type LearnabilityScoreGeneralOption = z.infer<
  typeof learnabilityScoreGeneralEnum
>
export type LearnabilityScoreWebFormeelOption = z.infer<
  typeof learnabilityScoreWebFormeelEnum
>
export type LearnabilityScoreOption =
  | LearnabilityScoreGeneralOption
  | LearnabilityScoreWebFormeelOption

export const learnabilityScoreGeneralOptions: {
  value: LearnabilityScoreGeneralOption
  label: string
}[] = [
  { value: "zero", label: "0" },
  { value: "one_to_twelve", label: "1 t/m 12" },
  { value: "thirteen_or_higher", label: "13 of hoger" },
  { value: "eighteen_or_higher", label: "18 of hoger" },
  { value: "twenty_or_higher", label: "20 of hoger" },
  { value: "not_applicable", label: "N.v.t." },
]

export const learnabilityScoreWebFormeelOptions: {
  value: LearnabilityScoreWebFormeelOption
  label: string
}[] = [
  { value: "ten_to_fifteen", label: "10 t/m 15" },
  { value: "sixteen_or_higher", label: "16 of hoger" },
  { value: "twenty_or_higher", label: "20 of hoger" },
  { value: "not_applicable", label: "N.v.t." },
]

export const yearsOfEducationEnum = z.enum(
  [
    "less_than_6_years",
    "at_least_6_years",
    "at_least_10_years",
    "at_least_15_years",
    "not_applicable",
  ],
  { message: "Aantal jaar onderwijs is verplicht" },
)

export type YearsOfEducationOption = z.infer<typeof yearsOfEducationEnum>

export const yearsOfEducationOptions: {
  value: YearsOfEducationOption
  label: string
}[] = [
  { value: "less_than_6_years", label: "Minder dan 6 jaar" },
  { value: "at_least_6_years", label: "6 jaar of meer" },
  { value: "at_least_10_years", label: "10 jaar of meer" },
  { value: "at_least_15_years", label: "15 jaar of meer" },
  { value: "not_applicable", label: "N.v.t." },
]

export type LearningPathRoute = TaaloffensiefRoute | null

export interface LearningPathStartLetter {
  id: number
  displayFilename: string
  filename: string
}

export interface LearningPath {
  id: number
  route: LearningPathRoute
  learningPath: LearningPathOption
  category: LearningPathCategory
  learnabilityScore: LearnabilityScoreOption | null
  yearsOfEducation?: YearsOfEducationOption
  homeworkHours?: number | null
  endGoal: string
  capacity: number
  alphabetized: boolean
  insertedAt: string
  updatedAt: string
  startLetter?: LearningPathStartLetter | null
}

export type LearningPathApiResponse = LearningPath

export interface LearningPathWithModules {
  data: LearningPathApiResponse & {
    modules: Module[]
  }
}
export interface CreateLearningPathApiRequest {
  learningPath: Omit<
    LearningPathApiResponse,
    "id" | "insertedAt" | "updatedAt" | "name"
  > & {
    modules: CreateModuleApiRequest[]
  }
}
export interface LearningPathsFiltersRequest {
  filters: Filters
}
export interface LearningPathsApiResponse {
  data: {
    learningPaths: LearningPathApiResponse[]
  }
  meta: MetaData
}
